import React, { useEffect, useState } from 'react';
import { Alert, Box, Breadcrumbs, Card, CardContent, CardHeader, CircularProgress, Fab, Link, MenuItem, TextField, Tooltip } from '@mui/material';
import Grid from '@mui/system/Unstable_Grid';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import axios from 'axios';
import { MdiIcon } from '@components';
import { mdiContentSave } from '@mdi/js';
const OblerProcessosCreateView = ({ $state, UIService, OblProcessoService }) => {
    const [loading, setLoading] = useState(false);
    const [codPostal, setCodPostal] = useState(false);
    const [parceiros, setParceiros] = useState([]);
    const [tecnicos, setTecnicos] = useState([]);
    const [valencias, setValencias] = useState([]);
    const [lotes, setLotes] = useState([]);
    // Autocomplete
    const [selected, setSelected] = useState(undefined);
    const fetchData = async () => {
        setLoading(true);
        // Técnicos
        const rTecnicos = await OblProcessoService.findTecnicos();
        setTecnicos(rTecnicos);
        // Valências
        const rValencias = await OblProcessoService.getValencias();
        setValencias(rValencias);
        // Parceiros
        const rClientes = await OblProcessoService.getClientes();
        setParceiros(rClientes);
        // Lotes
        const rLotes = await OblProcessoService.getLotes();
        setLotes(rLotes);
        setLoading(false);
    };
    const validationSchema = yup.object({
        agendamento: yup.date().required('O campo é obrigatório'),
        valenciaId: yup
            .number()
            .oneOf(valencias.map(x => x.id), 'Preencha um cliente válido')
            .required('O campo é obrigatório'),
        clienteId: yup
            .number()
            .oneOf(parceiros.map(x => x.id), 'Preencha um cliente válido')
            .required('O campo é obrigatório'),
        tecnicoId: yup
            .number()
            .oneOf(tecnicos.map(x => x.id), 'Preencha um técnico válido')
            .required('O campo é obrigatório'),
        nomeCliente: yup.string().required('O campo é obrigatório').max(255, 'Limite de caracteres ultrapassado'),
        nifCliente: yup
            .string()
            .nullable()
            .matches(/^(1|2|3|5|6|8|9)\d{8}$/, 'NIF inválido'),
        emailCliente: yup
            .string()
            .max(255, 'Limite de caracteres ultrapassado')
            .email('Email inválido')
            .nullable()
            .matches(/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, 'Email inválido'),
        morada: yup.string().required('O campo é obrigatório').max(255, 'Limite de caracteres ultrapassado'),
        cep: yup
            .string()
            .required('O campo é obrigatório')
            .matches(/^\d{4}-\d{3}$/, 'Código Postal deve estar no formato XXXX-YYY'),
        localidade: yup.string().required('O campo é obrigatório'),
        concelho: yup.string().required('O campo é obrigatório'),
        distrito: yup
            .string()
            .required('O campo é obrigatório')
            .test('is-valid-distrito', 'Distrito sem lote associado', value => {
            return lotes.some(l => l.distrito.toUpperCase() == value.toUpperCase());
        })
    });
    const formik = useFormik({
        initialValues: {},
        validationSchema: validationSchema,
        validateOnMount: false,
        validateOnChange: true,
        onSubmit: async (values) => {
            let wait = UIService.showWaiting();
            const data = await OblProcessoService.createProcesso(values);
            wait.close();
            if (data) {
                UIService.addToast('Processo criado com sucesso');
                $state.go('app.obler.processos.details', { id: data.id });
            }
            else {
                UIService.addToast('Não foi possível criar processo');
            }
        }
    });
    const getInfoCodigoPostal = async (cp4, cp3) => {
        return new Promise((resolve, reject) => {
            if (cp4.length === 4) {
                if (cp3.length === 3) {
                    axios
                        .post('/api/public/getLocalidade', {
                        cp4: cp4,
                        cp3: cp3
                    })
                        .then(r => {
                        resolve(r.data);
                    })
                        .catch(e => {
                        reject(e);
                    });
                }
                else {
                    axios
                        .post('/api/public/getLocalidade', {
                        cp4: cp4,
                        cp3: null
                    })
                        .then(r => {
                        resolve(r.data);
                    })
                        .catch(e => {
                        reject(e);
                    });
                }
            }
        });
    };
    const handleCEPChange = async (event) => {
        formik.setFieldValue('cep', event.target.value, true);
        formik.setFieldValue('loteId', undefined, true);
        const regex = /^\d{4}-\d{3}$/;
        setCodPostal(true);
        if (event.target.value.length === 8 && regex.test(event.target.value)) {
            try {
                let cps = event.target.value.split('-');
                let cep = await getInfoCodigoPostal(cps[0], cps[1]);
                if (cep) {
                    formik.setFieldValue('localidade', cep.result, true);
                    formik.setFieldValue('concelho', cep.concelho, true);
                    formik.setFieldValue('distrito', cep.distrito, true);
                }
                const lote = lotes.find(r => r.distrito.toUpperCase() == cep.distrito.toUpperCase());
                if (lote) {
                    formik.setFieldValue('loteId', lote.id, true);
                }
                else {
                    formik.setFieldValue('loteId', undefined, true);
                }
            }
            catch (error) {
                console.log(error);
            }
        }
        setCodPostal(false);
    };
    useEffect(() => {
        if (!loading)
            fetchData();
    }, []);
    return (<Box>
      <Box sx={{ mt: 2, mb: 2 }}>
        <Breadcrumbs aria-label='breadcrumb'>
          <Link underline='none' color='inherit'>
            Obler
          </Link>
          <Link underline='hover' color='text.primary' href={$state.href('app.obler.processos.list')}>
            Processos
          </Link>
          <Link underline='hover' color='text.primary' aria-current='page'>
            Adicionar
          </Link>
        </Breadcrumbs>
      </Box>
      {loading ? (<Box sx={{ display: 'flex', justifyContent: 'center', height: '2vh' }}>
          <CircularProgress size={60} color='primary'/>
        </Box>) : (<form>
          <Alert variant='filled' severity='info'>
            Todos os campos marcados com <b>*</b> são de preenchimento obrigatório
          </Alert>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid lg={12} md={12} xs={12}>
              <Card>
                <CardHeader title='Detalhes'/>
                <CardContent>
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid lg={6} md={6} xs={12}>
                      <TextField label='Parceiro' name='clienteId' value={formik.values?.clienteId} onChange={formik.handleChange} onBlur={formik.handleBlur} fullWidth error={formik.touched.clienteId && Boolean(formik.errors.clienteId)} helperText={formik.touched.clienteId && formik.errors.clienteId} select>
                        <MenuItem value={undefined} disabled>
                          Escolha uma opção da lista
                        </MenuItem>
                        {parceiros.map(x => (<MenuItem value={x.id} key={x.id}>
                            {x.nome}
                          </MenuItem>))}
                      </TextField>
                    </Grid>
                    <Grid lg={6} md={6} xs={12}>
                      <TextField label='Núm. Parceiro' name='parceiro' value={formik.values?.parceiro} onChange={formik.handleChange} onBlur={formik.handleBlur} fullWidth error={formik.touched.parceiro && Boolean(formik.errors.parceiro)} helperText={formik.touched.parceiro && formik.errors.parceiro}/>
                    </Grid>
                    <Grid lg={4} md={4} xs={12}>
                      <TextField label='Valência' name='valenciaId' value={formik.values?.valenciaId} onChange={formik.handleChange} onBlur={formik.handleBlur} fullWidth error={formik.touched.valenciaId && Boolean(formik.errors.valenciaId)} helperText={formik.touched.valenciaId && formik.errors.valenciaId} select>
                        <MenuItem value={undefined} disabled>
                          Escolha uma opção da lista
                        </MenuItem>
                        {valencias.map(x => (<MenuItem value={x.id} key={x.id}>
                            {x.categoria.designacao} -{x.designacao}
                          </MenuItem>))}
                      </TextField>
                    </Grid>
                    <Grid lg={8} md={8} xs={12}>
                      <TextField label='Observações' name='observacoes' value={formik.values?.observacoes} onChange={formik.handleChange} onBlur={formik.handleBlur} fullWidth error={formik.touched.observacoes && Boolean(formik.errors.observacoes)} helperText={formik.touched.observacoes && formik.errors.observacoes}/>
                    </Grid>
                    <Grid lg={8} md={8} xs={12}>
                      <TextField label='Nome do cliente' name='nomeCliente' value={formik.values?.nomeCliente} onChange={formik.handleChange} onBlur={formik.handleBlur} fullWidth required error={formik.touched.nomeCliente && Boolean(formik.errors.nomeCliente)} helperText={formik.touched.nomeCliente && formik.errors.nomeCliente}/>
                    </Grid>
                    <Grid lg={4} md={4} xs={12}>
                      <TextField label='NIF' name='nifCliente' defaultValue={formik.values?.nifCliente} onChange={formik.handleChange} onBlur={formik.handleBlur} fullWidth error={formik.touched.nifCliente && Boolean(formik.errors.nifCliente)} helperText={formik.touched.nifCliente && formik.errors.nifCliente}/>
                    </Grid>
                    <Grid lg={6} md={6} xs={12}>
                      <TextField label='Contacto' name='tlmCliente' value={formik.values?.tlmCliente} onChange={formik.handleChange} onBlur={formik.handleBlur} fullWidth error={formik.touched.tlmCliente && Boolean(formik.errors.tlmCliente)} helperText={formik.touched.tlmCliente && formik.errors.tlmCliente}/>
                    </Grid>
                    <Grid lg={6} md={6} xs={12}>
                      <TextField label='E-mail' name='emailCliente' value={formik.values?.emailCliente} onChange={formik.handleChange} onBlur={formik.handleBlur} fullWidth error={formik.touched.emailCliente && Boolean(formik.errors.emailCliente)} helperText={formik.touched.emailCliente && formik.errors.emailCliente}/>
                    </Grid>
                  </Grid>
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid lg={12} md={12} xs={12}>
                      <TextField label='Morada' name='morada' value={formik.values?.morada} onChange={formik.handleChange} onBlur={formik.handleBlur} fullWidth required error={formik.touched.morada && Boolean(formik.errors.morada)} helperText={formik.touched.morada && formik.errors.morada}/>
                    </Grid>
                  </Grid>
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid lg={3} md={2} xs={12}>
                      <TextField label='Código Postal' name='cep' value={formik.values?.cep} onChange={handleCEPChange} onBlur={formik.handleBlur} fullWidth required error={formik.touched.cep && Boolean(formik.errors.cep)} helperText={formik.touched.cep && formik.errors.cep}/>
                    </Grid>
                    <Grid lg={3} md={4} xs={12}>
                      <TextField label='Localidade' name='localidade' value={formik.values?.localidade ?? ''} onChange={formik.handleChange} onBlur={formik.handleBlur} fullWidth required error={formik.touched.localidade && Boolean(formik.errors.localidade)} helperText={formik.touched.localidade && formik.errors.localidade} disabled={codPostal} InputProps={{
                endAdornment: <React.Fragment>{codPostal ? <CircularProgress color='inherit' size={20}/> : null}</React.Fragment>
            }}/>
                    </Grid>
                    <Grid lg={3} md={3} xs={12}>
                      <TextField label='Concelho' name='processo.concelhoCliente' value={formik.values?.concelho ?? ''} onChange={formik.handleChange} onBlur={formik.handleBlur} fullWidth required error={formik.touched.concelho && Boolean(formik.errors.concelho)} helperText={formik.touched.concelho && formik.errors.concelho} disabled={codPostal} InputProps={{
                endAdornment: <React.Fragment>{codPostal ? <CircularProgress color='inherit' size={20}/> : null}</React.Fragment>
            }}/>
                    </Grid>
                    <Grid lg={3} md={3} xs={12}>
                      <TextField label='Distrito' name='distrito' value={formik.values?.distrito ?? ''} onChange={formik.handleChange} onBlur={formik.handleBlur} fullWidth required error={formik.touched.distrito && Boolean(formik.errors.distrito)} helperText={formik.touched.distrito && formik.errors.distrito} disabled={codPostal} InputProps={{
                endAdornment: <React.Fragment>{codPostal ? <CircularProgress color='inherit' size={20}/> : null}</React.Fragment>
            }}/>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            <Grid lg={12} md={12} xs={12}>
              <Card>
                <CardHeader title='Agendamento'/>
                <CardContent>
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid lg={4} md={12} xs={12}>
                      <TextField label='Tipo de agendamento' name='tipoId' value={formik.values?.tipoId ?? ''} onChange={formik.handleChange} onBlur={formik.handleBlur} fullWidth required error={formik.touched.tipoId && Boolean(formik.errors.tipoId)} helperText={formik.touched.tipoId && formik.errors.tipoId} select>
                        <MenuItem value={undefined} disabled></MenuItem>
                        {[
                {
                    id: 2,
                    name: 'Orçamento'
                },
                {
                    id: 3,
                    name: 'Visita técnica'
                }
            ].map(x => (<MenuItem value={x.id} key={x.id}>
                            {x.name}
                          </MenuItem>))}
                      </TextField>
                    </Grid>
                    <Grid lg={4} md={4} xs={12}>
                      <LocalizationProvider dateAdapter={AdapterMoment}>
                        <DateTimePicker label='Data/Hora' format='DD/MM/YYYY HH:mm' value={formik.values?.agendamento} onChange={value => {
                formik.setFieldValue('agendamento', value, true);
            }} slotProps={{
                textField: {
                    variant: 'outlined',
                    name: 'data',
                    onBlur: formik.handleBlur,
                    error: formik.touched.agendamento && Boolean(formik.errors.agendamento),
                    fullWidth: true,
                    helperText: formik.touched.agendamento && Boolean(formik.errors.agendamento) && String(formik.errors.agendamento) // Use boolean cause string([]) = "undefined"
                }
            }}/>
                      </LocalizationProvider>
                    </Grid>
                    <Grid lg={4} md={8} xs={12}>
                      <TextField label='Técnico' name='tecnicoId' value={formik.values?.tecnicoId} onChange={formik.handleChange} onBlur={formik.handleBlur} fullWidth error={formik.touched.tecnicoId && Boolean(formik.errors.tecnicoId)} helperText={formik.touched.tecnicoId && formik.errors.tecnicoId} select>
                        <MenuItem value={undefined} disabled></MenuItem>
                        {tecnicos &&
                tecnicos.map(x => (<MenuItem value={x.id} key={x.id}>
                              {x.name}
                            </MenuItem>))}
                      </TextField>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          <Box sx={{
                position: 'fixed',
                margin: 2,
                bottom: 0,
                right: 0,
                display: 'flex',
                gap: 1
            }}>
            <Tooltip title='Adicionar'>
              <Fab color='primary' disabled={!formik.isValid || !formik.dirty || formik.isSubmitting} onClick={() => {
                formik.handleSubmit();
            }}>
                <MdiIcon path={mdiContentSave}/>
              </Fab>
            </Tooltip>
          </Box>
        </form>)}
    </Box>);
};
export default OblerProcessosCreateView;
